/**
 * TikTok 认证相关 API
 */
import apiClient from './index'

/**
 * TikTok 认证 API 类
 */
export class TikTokAPI {
  /**
   * 发起 TikTok 登录
   * @returns {Promise} 返回包含授权 URL 的响应
   */
  static async initiateLogin() {
    try {
      const response = await apiClient.get('/api/v1/auth/login')
      
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message
      }
    }
  }

  /**
   * 处理 TikTok 授权回调
   * @param {string} code 授权码
   * @param {string} state 状态令牌
   * @returns {Promise} 返回令牌信息
   */
  static async handleCallback(code, state) {
    try {
      const response = await apiClient.get('/api/v1/auth/callback', {
        params: { code, state }
      })
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message
      }
    }
  }

  /**
   * 刷新访问令牌
   * @param {string} refreshToken 刷新令牌
   * @returns {Promise} 返回新的令牌信息
   */
  static async refreshToken(refreshToken) {
    try {
      const response = await apiClient.post('/api/v1/auth/refresh', {
        refresh_token: refreshToken
      })
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message
      }
    }
  }

  /**
   * 获取用户信息
   * @returns {Promise} 返回用户信息
   */
  static async getUserInfo() {
    try {
      const response = await apiClient.get('/api/v1/user/info')
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message
      }
    }
  }

  /**
   * 检查服务健康状态
   * @returns {Promise} 返回健康状态
   */
  static async checkHealth() {
    try {
      const response = await apiClient.get('/api/v1/health')
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || error.message
      }
    }
  }

  /**
   * 上传视频到TikTok
   * @param {Object} uploadData 上传数据
   * @param {string} uploadData.title 视频标题
   * @param {File} uploadData.videoFile 视频文件
   * @param {string} uploadData.privacyLevel 隐私级别
   * @param {boolean} uploadData.disableDuet 是否禁用合拍
   * @param {boolean} uploadData.disableComment 是否禁用评论
   * @param {boolean} uploadData.disableStitch 是否禁用缝合
   * @param {number} uploadData.videoCoverTimestampMs 视频封面时间戳
   * @param {number} uploadData.chunkSize 分块大小
   * @param {Function} onProgress 上传进度回调
   * @returns {Promise} 返回上传结果
   */
  static async uploadVideo(uploadData, onProgress) {
    try {
      // 获取访问令牌
      const accessToken = TokenManager.getAccessToken()
      if (!accessToken) {
        throw new Error('未找到访问令牌，请先登录')
      }

      const formData = new FormData()
      
      // 添加访问令牌 (根据后端要求作为表单参数)
      formData.append('accessToken', accessToken)
      
      // 添加必需参数
      formData.append('title', uploadData.title)
      formData.append('videoFile', uploadData.videoFile)
      
      // 添加可选参数
      formData.append('privacyLevel', uploadData.privacyLevel || 'PUBLIC_TO_EVERYONE')
      formData.append('disableDuet', uploadData.disableDuet || 'false')
      formData.append('disableComment', uploadData.disableComment || 'false')
      formData.append('disableStitch', uploadData.disableStitch || 'false')
      formData.append('videoCoverTimestampMs', uploadData.videoCoverTimestampMs || '1000')
      
      if (uploadData.chunkSize) {
        formData.append('chunkSize', uploadData.chunkSize.toString())
      }

      console.log('=== 视频上传开始 ===')
      console.log('标题:', uploadData.title)
      console.log('文件名:', uploadData.videoFile.name)
      console.log('文件大小:', uploadData.videoFile.size)
      console.log('隐私级别:', uploadData.privacyLevel)
      console.log('访问令牌:', accessToken ? '已提供' : '未提供')

      const response = await apiClient.post('/api/v2/video/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 0, // 文件上传不设置超时限制
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(percentCompleted)
          }
        }
      })

      console.log('视频上传成功:', response.data)
      
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('视频上传失败:', error)
      return {
        success: false,
        error: error.response?.data || error.message
      }
    }
  }
}

/**
 * 令牌管理工具类
 */
export class TokenManager {
  /**
   * 保存令牌信息
   * @param {Object} tokenData 令牌数据
   */
  static saveTokens(tokenData) {
    console.log('=== TokenManager: 保存令牌 ===')
    console.log('接收到的令牌数据:', tokenData)
    
    if (tokenData.access_token) {
      localStorage.setItem('tiktok_access_token', tokenData.access_token)
      console.log('已保存 access_token')
    } else {
      console.warn('没有 access_token')
    }
    
    if (tokenData.refresh_token) {
      localStorage.setItem('tiktok_refresh_token', tokenData.refresh_token)
      console.log('已保存 refresh_token')
    } else {
      console.warn('没有 refresh_token')
    }
    
    if (tokenData.expires_in) {
      const expiresAt = Date.now() + (tokenData.expires_in * 1000)
      localStorage.setItem('tiktok_token_expires_at', expiresAt.toString())
      console.log('已保存过期时间:', new Date(expiresAt).toLocaleString())
    } else {
      console.warn('没有 expires_in')
    }
    
    console.log('令牌保存完成')
  }

  /**
   * 获取访问令牌
   * @returns {string|null} 访问令牌
   */
  static getAccessToken() {
    return localStorage.getItem('tiktok_access_token')
  }

  /**
   * 获取刷新令牌
   * @returns {string|null} 刷新令牌
   */
  static getRefreshToken() {
    return localStorage.getItem('tiktok_refresh_token')
  }

  /**
   * 检查令牌是否过期
   * @returns {boolean} 是否过期
   */
  static isTokenExpired() {
    const expiresAt = localStorage.getItem('tiktok_token_expires_at')
    if (!expiresAt) return true
    return Date.now() > parseInt(expiresAt)
  }

  /**
   * 清除所有令牌
   */
  static clearTokens() {
    localStorage.removeItem('tiktok_access_token')
    localStorage.removeItem('tiktok_refresh_token')
    localStorage.removeItem('tiktok_token_expires_at')
    localStorage.removeItem('tiktok_user_info')
  }

  /**
   * 保存用户信息
   * @param {Object} userInfo 用户信息
   */
  static saveUserInfo(userInfo) {
    localStorage.setItem('tiktok_user_info', JSON.stringify(userInfo))
  }

  /**
   * 获取用户信息
   * @returns {Object|null} 用户信息
   */
  static getUserInfo() {
    const userInfo = localStorage.getItem('tiktok_user_info')
    return userInfo ? JSON.parse(userInfo) : null
  }

  /**
   * 检查是否已登录
   * @returns {boolean} 是否已登录
   */
  static isLoggedIn() {
    const token = this.getAccessToken()
    const isExpired = this.isTokenExpired()
    
    console.log('=== TokenManager: 检查登录状态 ===')
    console.log('access_token 存在:', !!token)
    console.log('令牌是否过期:', isExpired)
    
    const loggedIn = token && !isExpired
    console.log('最终登录状态:', loggedIn)
    
    return loggedIn
  }
}

export default TikTokAPI 